import React, { useState, useRef } from "react";
import {
  Box, VStack, Flex, IconButton, Tooltip, Spinner,
  useOutsideClick, Skeleton
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { AuthenticatedImage } from "./AuthenticatedImage";
import { motion, AnimatePresence } from "framer-motion";

interface ProjectSidebarImagesProps {
  images: string[];
  selectedImageUrl?: string | null;
  onSelectImage?: (url: string) => void;
  deletableUrls?: Set<string>;
  onAddImage?: () => void;
  onDeleteImage?: (url: string) => void;
  isUploading?: boolean;
  token?: string;
  deletingImage?: string | null;
}

const ProjectSidebarImages: React.FC<ProjectSidebarImagesProps> = ({
  images,
  selectedImageUrl = null,
  onSelectImage,
  deletableUrls,
  onAddImage,
  onDeleteImage,
  isUploading = false,
  token,
  deletingImage
}) => {
  const { t } = useTranslation();
  const sidebarBg = "gray.900";
  const buttonBg = "gray.800";
  const hoverBg = "gray.700";
  
  const [localSelected, setLocalSelected] = useState<string | null>(null);
  const containerRef = useRef(null);
  const isSelected = (url: string) => selectedImageUrl !== undefined && selectedImageUrl !== null ? selectedImageUrl === url : localSelected === url;
  const canDelete = deletableUrls ? (url: string) => deletableUrls.has(url) : () => true;

  useOutsideClick({
    ref: containerRef,
    handler: () => setLocalSelected(null),
  });

  const handleThumbClick = (url: string) => {
    if (onSelectImage) {
      onSelectImage(url);
    } else {
      setLocalSelected(localSelected === url ? null : url);
    }
    if (deletingImage === url) return;
  };

  /* Hauteur pour afficher 5 miniatures (50px + 16px spacing) × 5 ≈ 330px */
  const thumbHeight = 50;
  const thumbSpacing = 16;
  const visibleCount = 5;
  const scrollAreaHeight = visibleCount * thumbHeight + (visibleCount - 1) * thumbSpacing;

  return (
    <Flex
      ref={containerRef}
      direction="column"
      align="center"
      gap={0}
      mr={6}
      position="sticky"
      top="20px"
      zIndex={10}
      w="80px"
    >
      {/* Boîte du + à part, toujours visible (décalée en haut) */}
      <Box
        bg={sidebarBg}
        borderRadius="30px 30px 0 0"
        py={4}
        px={2}
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="lg"
        w="100%"
      >
        <Tooltip label={t("addImages") || "Ajouter des images"} placement="right">
          <IconButton
            aria-label="Add image"
            icon={isUploading ? <Spinner size="sm" color="white" /> : <AddIcon color="white" />}
            size="lg"
            isRound
            bg={buttonBg}
            _hover={{ bg: hoverBg }}
            onClick={onAddImage}
            w="50px"
            h="50px"
            boxShadow="md"
            isDisabled={isUploading}
          />
        </Tooltip>
      </Box>

      {/* Boîte des miniatures : 5 visibles, le reste en scroll */}
      <Box
        bg={sidebarBg}
        borderRadius="0 0 30px 30px"
        py={4}
        px={2}
        boxShadow="lg"
        w="100%"
        maxH={`${scrollAreaHeight + 32}px`}
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        <Box overflowY="auto" overflowX="hidden" w="100%" flex="1" minH={0} maxH={`${scrollAreaHeight}px`}>
        <VStack spacing={4} w="100%">
        <AnimatePresence mode="popLayout">
        {images && images.map((imgUrl, index) => {
          const selected = isSelected(imgUrl);
          const isDeleting = deletingImage === imgUrl;
          const showDelete = onDeleteImage && canDelete(imgUrl);
          
          return (
          <motion.div
            key={imgUrl}
            layout
            initial={{ opacity: 0 }}
            animate={isDeleting ? { opacity: [0.5, 0.8, 0.5] } : { opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            transition={isDeleting ? { repeat: Infinity, duration: 1.5 } : { duration: 0.2 }}
            style={{ position: "relative", width: "50px", height: "50px" }}
          >
            <Box
              w="50px"
              h="50px"
              borderRadius="12px"
              overflow="hidden"
              border="2px solid"
              borderColor={selected ? "blue.400" : "transparent"}
              _hover={{ borderColor: "blue.400", cursor: "pointer" }}
              transition="all 0.2s"
              onClick={() => !isDeleting && handleThumbClick(imgUrl)}
              position="relative"
              zIndex={2}
              bg="gray.800"
            >
              <AuthenticatedImage
                url={imgUrl}
                token={token}
                alt={`Project image ${index + 1}`}
                w="100%"
                h="100%"
                objectFit="cover"
              />
              
              {/* Shimmer Overlay */}
              {isDeleting && (
                  <Skeleton 
                    position="absolute" 
                    inset={0} 
                    startColor="whiteAlpha.100" 
                    endColor="whiteAlpha.400"
                    speed={1}
                  />
              )}
            </Box>

            {/* Context Menu (Slide out) */}
            <AnimatePresence>
            {(selected || isDeleting) && showDelete && (
                <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                    style={{
                        position: 'absolute',
                        left: '60px', // Right of the 50px image
                        top: '5px',
                        zIndex: 1,
                        background: 'rgba(26, 32, 44, 0.9)',
                        borderRadius: '10px',
                        padding: '5px',
                        backdropFilter: 'blur(5px)',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                    }}
                >
                     <Tooltip label={t("deleteImage") || "Supprimer"} placement="right">
                        <IconButton
                            aria-label="Delete"
                            icon={isDeleting ? <Spinner size="xs" color="red.400" /> : <DeleteIcon color="red.400" />}
                            size="sm"
                            variant="ghost"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            isDisabled={isDeleting}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteImage && onDeleteImage(imgUrl);
                            }}
                        />
                     </Tooltip>
                </motion.div>
            )}
            </AnimatePresence>
          </motion.div>
        )})}
        </AnimatePresence>
        </VStack>
        </Box>
      </Box>
    </Flex>
  );
};

export default ProjectSidebarImages;